import { useMemo, useState } from 'react';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { apiEndpoints } from '@utils/api-endpoints';
import tairraAxiosInstance, {
  fetcher,
  postFetcher,
  handlePostRequestWithArg,
  handleGetRequestWithArg,
  handlePutRequestWithArg,
  handleDeleteRequestWithArg,
} from 'src/utils/axios-tairra';
import { EMPLOYEE_ID_KEY } from '@utils/constants';
import { toast } from 'src/components/snackbar';
import { log } from '@utils/console-logger';

import { mutate } from 'swr';

const swrOptions = {
  revalidateIfStale: true,
  revalidateOnFocus: true,
  revalidateOnReconnect: true,
  revalidateOnMount: true,
  dedupingInterval: 5000,
};

export function useGetLoyaltyList() {
  const requestData = {
    sort: [{ direction: 'Desc', name: 'id' }],
    filters: [
      {
        filters: [
          { name: 'Name', operator: 'contains', value: '', logic: 'OR' },
          { name: 'Description', operator: 'contains', value: '', logic: 'OR' },
        ],
        logic: 'And',
      },
    ],
    value: true,
    logic: 'And',
    name: 'IsActive',
    operator: 'eq',
  };

  const { data, isLoading, error, isValidating } = useSWR(
    [apiEndpoints.loyalty.getLoyaltyList, requestData],
    postFetcher,
    swrOptions
  );

  const memoizedValue = useMemo(() => {
    const loyalty = data?.data ?? [];

    return {
      data: { loyalty },
      loyaltyLoading: isLoading,
      loyaltyError: error,
      loyaltyValidating: isValidating,
      loyaltyEmpty: !isLoading && !loyalty.length,
      mutateLoyaltyList: () => mutate([apiEndpoints.loyalty.getLoyaltyList]),
    };
  }, [data?.data, data?.total, error, isLoading, isValidating]);

  return memoizedValue;
}
export function useAddLoyalty() {
  const { trigger, isMutating, error } = useSWRMutation(
    apiEndpoints.loyalty.createVoucher,
    handlePostRequestWithArg
  );

  const addVoucher = async (requestData: any) => {
    // console.log("Request Data:", JSON.stringify(requestData));

    await trigger(requestData);
  };

  return {
    addVoucher,
    isLoading: isMutating,
    error,
  };
}
export function useDeleteVoucher() {
  const { data, error, mutate } = useSWR(
    (id: number) => (id ? `${apiEndpoints.loyalty.deleteVoucher}?id=${id}` : null),
    handleDeleteRequestWithArg,
    swrOptions
  );

  const deleteVoucher = async (id: number) => {
    // console.log('Deleting folder with ID:', id);
    if (id === undefined || id === null) {
      console.error('Invalid ID:', id);
      toast.error('Invalid ID provided');
      return;
    }
    try {
      const response = await handleDeleteRequestWithArg(
        `${apiEndpoints.loyalty.deleteVoucher}?id=${id}`,
        {
          method: 'DELETE',
        }
      );
      if (!response.ok) {
        throw new Error('Failed to delete voucher');
      }
      toast.success('Deleted Successfully');
      mutate();
    } catch (err) {
      console.error('Error deleting voucher:', err);
      // toast.error('Error deleting folder');
    }
  };

  return {
    deleteVoucher,
    isLoading: !data && !error,
    error,
  };
}
export function useGetRedeemStats() {
  const { data, isLoading, error, isValidating } = useSWR(
    [apiEndpoints.loyalty.getRedeemStats],
    fetcher,
    swrOptions
  );

  const memoizedValue = useMemo(() => {
    const redeemStats = data?.data ?? [];
    return {
      statsList: { redeemStats },
      documentsLoading: isLoading,
      documentsError: error,
      documentsValidating: isValidating,
      documentsEmpty: !isLoading && !redeemStats.length,
      mutateStats: () => mutate([apiEndpoints.loyalty.getRedeemStats]),
    };
  }, [data?.data, data?.data.length, error, isLoading, isValidating]);

  return memoizedValue;
}
export function useGetRedeemHistory(page: number, pageSize: number, employeeId: number) {
  const requestData = {
    sort: [{ name: 'id', direction: 'Desc' }],
    filter: {
      filters: [{ name: 'employeeId', operator: 'eq', value: employeeId, logic: 'AND' }],
    },
    page: page,
    pageSize: pageSize,
  };

  const { data, isLoading, error, isValidating } = useSWR(
    [apiEndpoints.loyalty.getRedeemHistory, requestData],
    postFetcher,
    swrOptions
  );

  const memoizedValue = useMemo(() => {
    const redeemHistory = data?.data ?? [];
    const totalRedeemed = data?.total;

    return {
      data: { redeemHistory, totalRedeemed },
      redeemHistoryLoading: isLoading,
      redeemHistoryError: error,
      redeemHistoryValidating: isValidating,
      redeemHistoryEmpty: !isLoading && !redeemHistory.length,
      mutateRedeemHistory: () =>
        mutate([apiEndpoints.loyalty.getRedeemHistory, employeeId, page, pageSize]),
    };
  }, [data?.data, data?.total, error, isLoading, isValidating]);

  return memoizedValue;
}
export function useGetRedeemItemHistory(page: number, pageSize: number, redemptionItemId: number) {
  const requestData = {
    sort: [{ name: 'id', direction: 'Desc' }],
    filter: {
      filters: [
        { name: 'redemptionItemId', operator: 'eq', value: redemptionItemId, logic: 'AND' },
      ],
    },
    page: page,
    pageSize: pageSize,
  };

  const { data, isLoading, error, isValidating } = useSWR(
    [apiEndpoints.loyalty.getRedeemItemHistory, requestData],
    postFetcher,
    swrOptions
  );

  const memoizedValue = useMemo(() => {
    const redeemItemHistory = data?.data ?? [];
    const totalRedeemedItem = data?.total;

    return {
      data: { redeemItemHistory, totalRedeemedItem },
      redeemItemHistoryLoading: isLoading,
      redeemItemHistoryError: error,
      redeemItemHistoryValidating: isValidating,
      redeemItemHistoryEmpty: !isLoading && !redeemItemHistory.length,
      mutateRedeemItemHistory: () =>
        mutate([apiEndpoints.loyalty.getRedeemItemHistory, redemptionItemId, page, pageSize]),
    };
  }, [data?.data, data?.total, error, isLoading, isValidating]);

  return memoizedValue;
}
export function useRedeem() {
  const { trigger, isMutating, error } = useSWRMutation(
    apiEndpoints.loyalty.redeemVoucher,
    handlePostRequestWithArg
  );

  const redeemVoucher = async (requestData: any) => {
    // console.log("Request Data:", JSON.stringify(requestData));

    await trigger(requestData);
  };

  return {
    redeemVoucher,
    isLoading: isMutating,
    error,
    mutateStats: () => mutate([apiEndpoints.loyalty.getRedeemStats]),
  };
}
export function useRedemApprove() {
  const { data, error, mutate } = useSWR(
    (id: number) => (id ? `${apiEndpoints.loyalty.redeemApprove}?id=${id}` : null),
    handlePostRequestWithArg
  );

  const redemApprove = async (id: number, status: number, reason: string) => {
    if (id === undefined || id === null) {
      console.error('Invalid ID:', id);
      toast.error('Invalid ID provided');
      return;
    }

    const requestData = {
      arg: {
        status: status,
        reason: reason || null,
      },
    };

    try {
      const response = await handlePostRequestWithArg(
        `${apiEndpoints.loyalty.redeemApprove}?id=${id}`,
        requestData
      );

      mutate();

      return response;
    } catch (err) {
      console.error('Error approving redemption:', err);
      toast.error('Error approving redemption');
      return [];
    }
  };

  return {
    redemApprove,
    isLoading: !data && !error,
    error,
  };
}
export function useVoucherConsume() {
  const { data, error, mutate } = useSWR(
    (id: number) => (id ? `${apiEndpoints.loyalty.redeemConsume}/?id=${id}` : null),
    handlePostRequestWithArg
  );

  const voucherConsume = async (id: number) => {
    if (!id) {
      console.error('Invalid ID:', id);
      toast.error('Invalid ID provided');
      return;
    }

    try {
      const response = await handlePostRequestWithArg(
        `${apiEndpoints.loyalty.redeemConsume}/?id=${id}`,
        { arg: { id } }
      );

      mutate();

      return response;
    } catch (err) {
      console.error('Error consuming redemption:', err);
      toast.error('Error consuming redemption');
      return null;
    }
  };

  return {
    voucherConsume,
    isLoading: !data && !error,
    error,
  };
}
export async function photoInsert(file: File) {
  if (!file) {
    toast.error('No Photo provided for upload.');
    return null;
  }

  try {
    const formData = new FormData();
    formData.append('Photo', file);

    const response = await tairraAxiosInstance.post(apiEndpoints.loyalty.photoInsert, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    if (response.status === 200 && response.data?.returnStatus) {
      toast.success('Photo uploaded successfully!');
      // console.log(response.data.data);
      return response.data.data;
    } else {
      toast.error('Photo upload failed.');
      console.error('Upload failed:', response.data);
      return null;
    }
  } catch (error) {
    toast.error('An error occurred while uploading the file.');
    console.error('Photo upload error:', error);
    return null;
  }
}
export function useGetVoucher(id: number) {
  const { data, error, isValidating } = useSWR(
    id !== undefined ? `${apiEndpoints.loyalty.updateLoyalty}/${id}` : null,
    fetcher,
    swrOptions
  );
  const memoizedNotesList = useMemo(() => {
    const voucherDetails = data?.data ?? [];

    const hasError = Boolean(error);

    return {
      voucherDetails,

      isVoucherLoading: !data && !error,
      voucherError: hasError ? error : null,
      voucherValidating: isValidating,
      voucherListNotFound: !data?.data?.length && !error,
      mutateVoucher: () => mutate([apiEndpoints.loyalty.updateLoyalty]),
    };
  }, [data?.data, data?.total, error, isValidating]);

  return memoizedNotesList;
}
export function useUpdateVoucher() {
  const { data, error } = useSWR(
    (id: number) => (id ? `${apiEndpoints.loyalty.updateVoucher}?id=${id}` : null),
    handlePutRequestWithArg
  );

  const updateVoucher = async (id: number, requestData: any) => {
    if (id === undefined || id === null) {
      return [];
    }
    try {
      const response = await handlePutRequestWithArg(
        `${apiEndpoints.loyalty.updateVoucher}?id=${id}`,
        requestData
      );
      const result = await response;
      return result;
    } catch (err) {
      console.error('Error sharing:', err);
      return [];
    }
  };

  return {
    updateVoucher,
    isLoading: !data && !error,
    error,
  };
}
export function useUpdateRedeemRequest() {
  const { data, error } = useSWR(
    (id: number) => (id ? `${apiEndpoints.loyalty.updateRedemptionHistory}?id=${id}` : null),
    handlePutRequestWithArg
  );

  const updateRedemptionHistory = async (id: number, requestData: any) => {
    if (id === undefined || id === null) {
      return false;
    }

    try {
      const response = await handlePutRequestWithArg(
        `${apiEndpoints.loyalty.updateRedemptionHistory}?id=${id}`,
        requestData
      );

      if (!response?.returnStatus) {
        const errorMessage = response?.returnMessage?.[0] || 'Failed to update request ';
        console.error(errorMessage);
        throw new Error(errorMessage);
      }

      toast.success('Request updated successfully');
      return true;
    } catch (err: any) {
      console.error('Error updating request:', err);
      toast.error(err?.returnMessage || 'An unexpected error occurred while updating the request');
      return false;
    }
  };

  return {
    updateRedemptionHistory,
    isLoading: !data && !error,
    error,
  };
}
